import s from './GigAssist.module.sass'

import { GigAssChat } from './components/GigAssChat'
import { GigAssFooter } from './components/GigAssFooter'
import { GigAssHeader } from './components/GigAssHeader'

import { Overlay } from './ui/Overlay/Overlay'

import { resetMessages } from './model/slice'
import { cancelMessageRequest } from './model/actions'

import { useAssistOpen } from './hooks/useAssistOpen'
import { useGigAssistContent } from './hooks/useGigAssistContent'
import { useLockScroll } from './hooks/useLockScroll'
import { useVisibleHeight } from './hooks/useVisibleHeight'

import useFeatureFlag from '@shared/lib/hooks/useFeatureFlag'
import useEscapeKey from './hooks/useEscapeKey'
import useShowActive from './hooks/useShowActive'

import { FC, useState, useEffect } from 'react'
import { FloatingPortal } from '@floating-ui/react'

import { useAppDispatch, useAppSelector } from '@app/model/store'

import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { GigAssPrompt } from './components/GigAssPrompt'
import { openModal } from '@widgets/Common/ModalFeedback/model/actions'
import { scrollChat } from '@widgets/Common/GigAssist/helpers/scrollChat'
import { EGG } from '@shared/api/analytics'

const ANIMATION_DURATION = 500 // мс

/**
 * @function GigAssist - корневой компонент, виджет ассистента.
 */
const GigAssist: FC = () => {
  const dispatch = useAppDispatch()
  const [isRotating, setIsRotating] = useState(false)

  const visibleHeight = useVisibleHeight() || null
  const { isMobile } = useClientResponsive()

  const { isOpen, setIsOpen } = useAssistOpen({ isAssistOpen: 'is_assist_open' })

  // Возможно нужна аналитика нажатия на Esc
  useEscapeKey(isOpen, () => setIsOpen(false))
  useLockScroll(isOpen)

  const [isStarted, setIsStarted] = useShowActive({ isOpen })
  useEffect(() => {
    setIsStarted(false)
  }, [])

  useEffect(() => {
    if (isOpen) {
      EGG.gigachat.page_shown()
    }
  }, [isOpen])

  useGigAssistContent({ contentId: 'next-gig-assist-content', visibleHeight, isMobile })

  const userData = useAppSelector((state) => !!state.global?.user?.data)
  const assistFlag = useFeatureFlag('gigachat')
  const isAssistantActive = userData || assistFlag

  if (!isAssistantActive) {
    return null
  }

  const handleReload = () => {
    setIsRotating(true)
    EGG.gigachat.reset_chat()

    // отменяем текущий запрос, если он выполняется
    dispatch(cancelMessageRequest())

    dispatch(resetMessages())

    // сбрасываем состояние анимации через 500мс
    setTimeout(() => {
      setIsRotating(false)

      // сначала прокручиваем к верху чата
      setTimeout(() => {
        scrollChat('start')
      }, 1000)
    }, ANIMATION_DURATION)
  }

  // * Открыть модалку с вопросом о найденной ошибке *
  const handleOpenModalFeedback = () => {
    setIsOpen(false)
    dispatch(openModal('aiAssistantQuestion'))
  }

  const baseStyles = {
    ...(ANIMATION_DURATION && { '--animation-duration': `${ANIMATION_DURATION}ms` }),
    ...(visibleHeight && { '--visible-height': `${visibleHeight}px` }),
  } as React.CSSProperties

  const handleClose = (closeAnalyticsHandler: () => void) => () => {
    closeAnalyticsHandler()
    setIsOpen(false)
  }

  return (
    <>
      {/* Открыто */}
      {isOpen && (
        <FloatingPortal id='next-gig-assist-content'>
          <div className={s.gigaBase} style={baseStyles}>
            <div className={`${s.modal} ${isStarted ? s.active : ''}`}>
              <Overlay className={s.modalOverlay} onClick={handleClose(EGG.gigachat.close_assistant_outside)} />

              <section className={`${s.modalContent} ${isStarted ? s.active : ''}`} data-name='gigAssContent'>
                <GigAssHeader
                  isRotating={isRotating}
                  onReload={handleReload}
                  onClose={handleClose(EGG.gigachat.close_assistant)}
                />

                <GigAssChat isShowActive={isStarted} />

                <GigAssPrompt isShowActive={isStarted} />

                <GigAssFooter onOpenFoundMistake={handleOpenModalFeedback} />
              </section>
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

export default GigAssist
